import React, { useState, useEffect, useRef } from "react";
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  SafeAreaView,
  ActivityIndicator,
  ScrollView,
  Dimensions,
  RefreshControl,
  Platform,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { useAuth } from "../../contexts/AuthContext";
import IssueReportsScreen from "./IssueReportsScreen";
import OpportunityListScreen from "./OpportunityListScreen";
import OpportunityEditorScreen from "./OpportunityEditorScreen";
import OpportunitySuggestionsScreen from "./OpportunitySuggestionsScreen";
import CrawledOpportunitiesScreen from "./CrawledOpportunitiesScreen";
import AnalyticsScreen from "./AnalyticsScreen";
import SuperAdminScreen from "./SuperAdminScreen";
import {
  getIssueReports,
  getOpportunitySuggestions,
  getCrawledOpportunities,
  getAllOpportunitiesForAdmin,
  getFeaturedOpportunityId,
  setFeaturedOpportunityId,
  getOpportunityById,
  initializeAIEmbeddings,
} from "../../services/parseService";

type AdminTab = "dashboard" | "opportunities" | "issues" | "suggestions" | "crawled" | "analytics" | "super";

interface AdminPanelProps {
  onClose: () => void;
}

interface DashboardStats {
  totalOpportunities: number;
  activeOpportunities: number;
  closedOpportunities: number;
  pendingIssues: number;
  pendingSuggestions: number;
  recentActivity: Array<{
    type: 'opportunity' | 'issue' | 'suggestion';
    title: string;
    time: string;
    status?: string;
  }>;
}

interface FeaturedOpportunityData {
  id: string | null;
  title: string | null;
}

const AdminPanel: React.FC<AdminPanelProps> = ({ onClose }) => {
  const [activeTab, setActiveTab] = useState<AdminTab>("dashboard");
  const [pendingIssuesCount, setPendingIssuesCount] = useState<number>(0);
  const [pendingSuggestionsCount, setPendingSuggestionsCount] = useState<number>(0);
  const [pendingCrawledCount, setPendingCrawledCount] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);
  const [selectedOpportunityId, setSelectedOpportunityId] = useState<string | null>(null);
  const [isEditorOpen, setIsEditorOpen] = useState<boolean>(false);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [featuredOpportunity, setFeaturedOpportunity] = useState<FeaturedOpportunityData>({
    id: null,
    title: null,
  });
  const [isChangingFeatured, setIsChangingFeatured] = useState<boolean>(false);
  const [dashboardStats, setDashboardStats] = useState<DashboardStats>({
    totalOpportunities: 0,
    activeOpportunities: 0,
    closedOpportunities: 0,
    pendingIssues: 0,
    pendingSuggestions: 0,
    recentActivity: [],
  });
  
  // AI Embeddings state
  const [isEmbeddingInProgress, setIsEmbeddingInProgress] = useState<boolean>(false);
  const [embeddingProgress, setEmbeddingProgress] = useState<string>("");
  const [showEmbeddingWarning, setShowEmbeddingWarning] = useState<boolean>(false);
  const [embeddingResult, setEmbeddingResult] = useState<{type: 'success' | 'error', message: string} | null>(null);

  const { currentUser } = useAuth();
  const screenWidth = Dimensions.get('window').width;
  const mountedRef = useRef(true);

  const userType = currentUser?.get("userType");
  const isAdmin = !!userType && ["admin", "super_admin"].includes(userType);
  const isSuperAdmin = userType === "super_admin";

  useEffect(() => {
    if (isAdmin) {
      loadDashboardData();
    }
    return () => {
      mountedRef.current = false;
    };
  }, [isAdmin]);

  const loadDashboardData = async () => {
    if (!mountedRef.current) return;
    console.log("Loading dashboard data");
    try {
      setIsLoading(true);
      setError(null);
      
      // Load all data in parallel
      const [issues, suggestions, crawled, opportunities, featuredId] = await Promise.all([
        getIssueReports("Pending"),
        getOpportunitySuggestions("Pending"),
        getCrawledOpportunities("Pending"),
        getAllOpportunitiesForAdmin(500),
        getFeaturedOpportunityId(),
      ]);

      if (!mountedRef.current) return;
      setPendingIssuesCount(issues.length);
      setPendingSuggestionsCount(suggestions.length);
      setPendingCrawledCount(crawled.length);

      // Load featured opportunity details
      if (featuredId) {
        try {
          const featuredOpp = await getOpportunityById(featuredId);
          setFeaturedOpportunity({
            id: featuredId,
            title: featuredOpp.get('title') || 'Unknown',
          });
        } catch (err) {
          console.error("Error loading featured opportunity:", err);
          setFeaturedOpportunity({ id: null, title: null });
        }
      } else {
        setFeaturedOpportunity({ id: null, title: null });
      }

      // Calculate opportunity stats
      const activeStatuses = ['Open', 'Ongoing', 'Coming Soon'];
      const activeOpps = opportunities.filter((opp: any) => 
        activeStatuses.includes(opp.attributes?.status || opp.status)
      );
      
      const closedOpps = opportunities.filter((opp: any) => 
        (opp.attributes?.status || opp.status) === 'Closed'
      );

      // Create recent activity
      const recentActivity = [
        ...issues.slice(0, 3).map((issue: any) => ({
          type: 'issue' as const,
          title: `Issue: ${issue.attributes?.issueType || 'Unknown'}`,
          time: formatRelativeTime(issue.createdAt),
          status: issue.attributes?.status || 'Pending',
        })),
        ...suggestions.slice(0, 3).map((suggestion: any) => ({
          type: 'suggestion' as const,
          title: suggestion.attributes?.title || 'New suggestion',
          time: formatRelativeTime(suggestion.createdAt),
          status: suggestion.attributes?.status || 'Pending',
        })),
        ...opportunities.slice(0, 2).map((opp: any) => ({
          type: 'opportunity' as const,
          title: `Updated: ${opp.attributes?.title || opp.title || 'Opportunity'}`,
          time: formatRelativeTime(opp.updatedAt || opp.attributes?.updatedAt),
          status: opp.attributes?.status || opp.status,
        })),
      ].sort((a, b) => new Date(b.time).getTime() - new Date(a.time).getTime()).slice(0, 8);

      setDashboardStats({
        totalOpportunities: opportunities.length,
        activeOpportunities: activeOpps.length,
        closedOpportunities: closedOpps.length,
        pendingIssues: issues.length,
        pendingSuggestions: suggestions.length,
        recentActivity,
      });

      setLastUpdated(new Date());

    } catch (err) {
      console.error("Error loading dashboard data:", err);
      if (mountedRef.current) {
        setError("Failed to load dashboard data. Please try again.");
      }
    } finally {
      if (mountedRef.current) {
        setIsLoading(false);
        setIsRefreshing(false);
      }
    }
  };

  const handleRefresh = () => {
    setIsRefreshing(true);
    loadDashboardData();
  };

  const formatRelativeTime = (date: any): string => {
    if (!date) return '';
    const now = new Date();
    const then = new Date(date.iso || date);
    const diffMs = now.getTime() - then.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);

    if (diffDays === 0) {
      if (diffHours === 0) return 'Just now';
      return `${diffHours}h ago`;
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return `${diffDays}d ago`;
    } else {
      return then.toLocaleDateString();
    }
  };

  const handleOpportunityEdit = (opportunityId: string | null) => {
    setSelectedOpportunityId(opportunityId);
    setIsEditorOpen(true);
  };

  const handleCloseEditor = () => {
    setIsEditorOpen(false);
    setSelectedOpportunityId(null);
    // Refresh data and stay on current tab
    loadDashboardData();
  };

  const handleRemoveFeatured = async () => {
    try {
      setIsChangingFeatured(true);
      const success = await setFeaturedOpportunityId(null);
      if (success) {
        setFeaturedOpportunity({ id: null, title: null });
        // Show success message (could use a toast/alert)
        console.log("Featured opportunity removed successfully");
      } else {
        setError("Failed to remove featured opportunity");
      }
    } catch (error) {
      console.error("Error removing featured opportunity:", error);
      setError("Error removing featured opportunity");
    } finally {
      setIsChangingFeatured(false);
    }
  };

  const handleInitializeEmbeddings = async () => {
    try {
      setIsEmbeddingInProgress(true);
      setEmbeddingProgress("Starting AI embedding initialization...");
      setEmbeddingResult(null);
      setShowEmbeddingWarning(false);
      
      const result = await initializeAIEmbeddings(true); // Force regeneration
      
      if (result.success) {
        setEmbeddingResult({ type: 'success', message: result.message });
        setEmbeddingProgress("Completed successfully!");
      } else {
        setEmbeddingResult({ type: 'error', message: result.message });
        setEmbeddingProgress("Failed to initialize embeddings");
      }
      
    } catch (error) {
      console.error("Error initializing embeddings:", error);
      setEmbeddingResult({ 
        type: 'error', 
        message: `Unexpected error: ${error instanceof Error ? error.message : String(error)}` 
      });
      setEmbeddingProgress("Failed with unexpected error");
    } finally {
      setIsEmbeddingInProgress(false);
    }
  };

  const confirmEmbeddingInitialization = () => {
    setShowEmbeddingWarning(false);
    handleInitializeEmbeddings();
  };

  const switchTab = (tab: AdminTab) => {
    setActiveTab(tab);
  };

  const exportData = () => {
    const data = {
      stats: dashboardStats,
      exportedAt: new Date().toISOString(),
      exportedBy: currentUser?.get("username") || "admin",
    };
    
    if (Platform.OS === 'web') {
      const dataStr = JSON.stringify(data, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `admin-dashboard-${new Date().toISOString().split('T')[0]}.json`;
      link.click();
      URL.revokeObjectURL(url);
    } else {
      console.log("Export data:", data);
      // On mobile, you could implement sharing or save to files
    }
  };

  const filteredActivity = dashboardStats.recentActivity.filter(activity =>
    activity.title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const renderDashboard = () => (
    <ScrollView 
      style={styles.dashboardContainer} 
      showsVerticalScrollIndicator={false}
      refreshControl={
        <RefreshControl
          refreshing={isRefreshing}
          onRefresh={handleRefresh}
          tintColor="#2563EB"
        />
      }
    >
      {/* Header with last updated */}
      <View style={styles.dashboardHeader}>
        <View>
          <Text style={styles.dashboardTitle}>Dashboard</Text>
          <Text style={styles.lastUpdated}>
            Last updated: {lastUpdated.toLocaleTimeString()}
          </Text>
        </View>
        <View style={styles.headerActions}>
          <TouchableOpacity 
            style={styles.exportButton}
            onPress={exportData}
            activeOpacity={0.7}
          >
            <Ionicons name="download-outline" size={18} color="#6B7280" />
          </TouchableOpacity>
          <TouchableOpacity 
            style={styles.refreshButton}
            onPress={handleRefresh}
            disabled={isRefreshing}
          >
            <Ionicons 
              name="refresh" 
              size={20} 
              color={isRefreshing ? "#9CA3AF" : "#2563EB"} 
            />
          </TouchableOpacity>
        </View>
      </View>

      {/* Error Banner */}
      {error && (
        <View style={styles.errorBanner}>
          <Ionicons name="warning-outline" size={18} color="#DC2626" />
          <Text style={styles.errorText}>{error}</Text>
          <TouchableOpacity onPress={() => setError(null)}>
            <Ionicons name="close" size={18} color="#DC2626" />
          </TouchableOpacity>
        </View>
      )}

      {/* Stats Grid */}
      <View style={styles.statsSection}>
        <Text style={styles.sectionTitle}>Overview</Text>
        <View style={styles.statsGrid}>
          <View style={styles.statCard}>
            <View style={styles.statContent}>
              <View style={styles.statInfo}>
                <Text style={styles.statNumber}>{dashboardStats.totalOpportunities}</Text>
                <Text style={styles.statLabel}>Total Opportunities</Text>
              </View>
              <View style={[styles.statIcon, { backgroundColor: '#EEF2FF' }]}>
                <Ionicons name="list-outline" size={24} color="#2563EB" />
              </View>
            </View>
          </View>

          <View style={styles.statCard}>
            <View style={styles.statContent}>
              <View style={styles.statInfo}>
                <Text style={styles.statNumber}>{dashboardStats.activeOpportunities}</Text>
                <Text style={styles.statLabel}>Active</Text>
              </View>
              <View style={[styles.statIcon, { backgroundColor: '#F0FDF4' }]}>
                <Ionicons name="checkmark-circle-outline" size={24} color="#16A34A" />
              </View>
            </View>
          </View>

          <View style={styles.statCard}>
            <View style={styles.statContent}>
              <View style={styles.statInfo}>
                <Text style={styles.statNumber}>{dashboardStats.closedOpportunities}</Text>
                <Text style={styles.statLabel}>Closed</Text>
              </View>
              <View style={[styles.statIcon, { backgroundColor: '#FEF2F2' }]}>
                <Ionicons name="close-circle-outline" size={24} color="#DC2626" />
              </View>
            </View>
          </View>

          <View style={styles.statCard}>
            <View style={styles.statContent}>
              <View style={styles.statInfo}>
                <Text style={styles.statNumber}>{dashboardStats.pendingIssues}</Text>
                <Text style={styles.statLabel}>Pending Issues</Text>
              </View>
              <View style={[styles.statIcon, { backgroundColor: '#FFFBEB' }]}>
                <Ionicons name="warning-outline" size={24} color="#D97706" />
              </View>
            </View>
          </View>
        </View>
      </View>

      {/* Featured Opportunity Section */}
      <View style={styles.featuredSection}>
        <Text style={styles.sectionTitle}>Featured Opportunity</Text>
        <View style={styles.featuredCard}>
          {featuredOpportunity.id ? (
            <>
              <View style={styles.featuredContent}>
                <View style={styles.featuredIcon}>
                  <Ionicons name="star" size={20} color="#F59E0B" />
                </View>
                <View style={styles.featuredInfo}>
                  <Text style={styles.featuredTitle} numberOfLines={1}>
                    {featuredOpportunity.title}
                  </Text>
                  <Text style={styles.featuredId}>ID: {featuredOpportunity.id}</Text>
                </View>
              </View>
              <View style={styles.featuredActions}>
                <TouchableOpacity
                  style={styles.featuredActionButton}
                  onPress={() => handleOpportunityEdit(featuredOpportunity.id)}
                  activeOpacity={0.7}
                >
                  <Ionicons name="create-outline" size={16} color="#2563EB" />
                  <Text style={styles.featuredActionText}>Edit</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[styles.featuredActionButton, styles.featuredRemoveButton]}
                  onPress={() => handleRemoveFeatured()}
                  activeOpacity={0.7}
                  disabled={isChangingFeatured}
                >
                  <Ionicons name="close-outline" size={16} color="#DC2626" />
                  <Text style={[styles.featuredActionText, { color: '#DC2626' }]}>Remove</Text>
                </TouchableOpacity>
              </View>
            </>
          ) : (
            <View style={styles.noFeaturedContainer}>
              <Ionicons name="star-outline" size={32} color="#9CA3AF" />
              <Text style={styles.noFeaturedText}>No featured opportunity set</Text>
              <TouchableOpacity
                style={styles.setFeaturedButton}
                onPress={() => setActiveTab("opportunities")}
                activeOpacity={0.7}
              >
                <Text style={styles.setFeaturedButtonText}>
                  Go to Opportunities to set one
                </Text>
              </TouchableOpacity>
            </View>
          )}
        </View>
      </View>

      {/* Quick Actions */}
      <View style={styles.quickActionsSection}>
        <Text style={styles.sectionTitle}>Quick Actions</Text>
        <View style={styles.quickActionsGrid}>
          <TouchableOpacity 
            style={styles.actionButton}
            onPress={() => handleOpportunityEdit(null)}
            activeOpacity={0.7}
          >
            <Ionicons name="add-outline" size={20} color="#2563EB" />
            <Text style={styles.actionButtonText}>Create Opportunity</Text>
          </TouchableOpacity>

          <TouchableOpacity 
            style={styles.actionButton}
            onPress={() => switchTab("opportunities")}
            activeOpacity={0.7}
          >
            <Ionicons name="create-outline" size={20} color="#2563EB" />
            <Text style={styles.actionButtonText}>Manage Opportunities</Text>
          </TouchableOpacity>

          {dashboardStats.pendingIssues > 0 && (
            <TouchableOpacity 
              style={[styles.actionButton, styles.actionButtonUrgent]}
              onPress={() => switchTab("issues")}
              activeOpacity={0.7}
            >
              <Ionicons name="bug-outline" size={20} color="#DC2626" />
              <Text style={[styles.actionButtonText, { color: '#DC2626' }]}>
                Review Issues ({dashboardStats.pendingIssues})
              </Text>
            </TouchableOpacity>
          )}

          {dashboardStats.pendingSuggestions > 0 && (
            <TouchableOpacity 
              style={styles.actionButton}
              onPress={() => switchTab("suggestions")}
              activeOpacity={0.7}
            >
              <Ionicons name="bulb-outline" size={20} color="#2563EB" />
              <Text style={styles.actionButtonText}>
                Review Suggestions ({dashboardStats.pendingSuggestions})
              </Text>
            </TouchableOpacity>
          )}
        </View>
      </View>

      {/* AI Embeddings Management */}
      <View style={styles.aiEmbeddingsSection}>
        <Text style={styles.sectionTitle}>AI Search Management</Text>
        <View style={styles.aiEmbeddingsCard}>
          <View style={styles.aiEmbeddingsHeader}>
            <View style={styles.aiEmbeddingsIcon}>
              <Ionicons name="sparkles" size={24} color="#8B5CF6" />
            </View>
            <View style={styles.aiEmbeddingsInfo}>
              <Text style={styles.aiEmbeddingsTitle}>OpenAI Embeddings</Text>
              <Text style={styles.aiEmbeddingsSubtitle}>
                Generate semantic embeddings for all opportunities
              </Text>
            </View>
          </View>
          
          {/* Warning Box */}
          <View style={styles.warningBox}>
            <Ionicons name="warning" size={16} color="#F59E0B" />
            <Text style={styles.warningText}>
              This will embed {dashboardStats.totalOpportunities} opportunities using OpenAI's text-embedding-3-small model.
              Estimated cost: ${(dashboardStats.totalOpportunities * 0.00002 * 200 / 1000).toFixed(2)}-${(dashboardStats.totalOpportunities * 0.00002 * 500 / 1000).toFixed(2)}
            </Text>
          </View>

          {/* Progress Display */}
          {isEmbeddingInProgress && (
            <View style={styles.progressContainer}>
              <ActivityIndicator size="small" color="#8B5CF6" />
              <Text style={styles.progressText}>{embeddingProgress}</Text>
            </View>
          )}

          {/* Result Display */}
          {embeddingResult && (
            <View style={[
              styles.resultContainer,
              embeddingResult.type === 'success' ? styles.resultSuccess : styles.resultError
            ]}>
              <Ionicons 
                name={embeddingResult.type === 'success' ? "checkmark-circle" : "close-circle"} 
                size={16} 
                color={embeddingResult.type === 'success' ? "#16A34A" : "#DC2626"} 
              />
              <Text style={[
                styles.resultText,
                { color: embeddingResult.type === 'success' ? "#16A34A" : "#DC2626" }
              ]}>
                {embeddingResult.message}
              </Text>
            </View>
          )}

          {/* Action Buttons */}
          <View style={styles.aiEmbeddingsActions}>
            <TouchableOpacity
              style={[styles.embeddingButton, styles.embeddingButtonDanger]}
              onPress={() => setShowEmbeddingWarning(true)}
              disabled={isEmbeddingInProgress}
              activeOpacity={0.7}
            >
              <Ionicons name="refresh" size={16} color="#DC2626" />
              <Text style={[styles.embeddingButtonText, { color: '#DC2626' }]}>
                Regenerate All Embeddings
              </Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>

      {/* Confirmation Modal */}
      {showEmbeddingWarning && (
        <View style={styles.modalOverlay}>
          <View style={styles.confirmationModal}>
            <View style={styles.modalHeader}>
              <Ionicons name="warning" size={32} color="#F59E0B" />
              <Text style={styles.modalTitle}>Confirm Embedding Regeneration</Text>
            </View>
            
            <View style={styles.modalContent}>
              <Text style={styles.modalText}>
                This action will regenerate embeddings for all {dashboardStats.totalOpportunities} opportunities.
              </Text>
              
              <View style={styles.costBreakdown}>
                <Text style={styles.costTitle}>💰 Estimated Cost:</Text>
                <Text style={styles.costItem}>• Model: text-embedding-3-small</Text>
                <Text style={styles.costItem}>• Rate: $0.00002 per 1K tokens</Text>
                <Text style={styles.costItem}>• Estimated tokens: {dashboardStats.totalOpportunities * 200}-{dashboardStats.totalOpportunities * 500}</Text>
                <Text style={styles.costTotal}>
                  Total: ${(dashboardStats.totalOpportunities * 0.00002 * 200 / 1000).toFixed(2)}-${(dashboardStats.totalOpportunities * 0.00002 * 500 / 1000).toFixed(2)}
                </Text>
              </View>
              
              <View style={styles.warningPoints}>
                <Text style={styles.warningPoint}>⚠️ This will overwrite existing embeddings</Text>
                <Text style={styles.warningPoint}>⚠️ Process may take 5-15 minutes</Text>
                <Text style={styles.warningPoint}>⚠️ Charges will apply to your OpenAI account</Text>
              </View>
            </View>
            
            <View style={styles.modalActions}>
              <TouchableOpacity
                style={styles.modalButtonCancel}
                onPress={() => setShowEmbeddingWarning(false)}
                activeOpacity={0.7}
              >
                <Text style={styles.modalButtonCancelText}>Cancel</Text>
              </TouchableOpacity>
              
              <TouchableOpacity
                style={styles.modalButtonConfirm}
                onPress={confirmEmbeddingInitialization}
                activeOpacity={0.7}
              >
                <Text style={styles.modalButtonConfirmText}>Proceed</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      )}

      {/* Recent Activity */}
      <View style={styles.activitySection}>
        <View style={styles.activityHeader}>
          <Text style={styles.sectionTitle}>Recent Activity</Text>
          {dashboardStats.recentActivity.length > 0 && (
            <TouchableOpacity 
              style={styles.searchButton}
              onPress={() => {/* Could implement search modal */}}
            >
              <Ionicons name="search-outline" size={16} color="#6B7280" />
            </TouchableOpacity>
          )}
        </View>
        <View style={styles.activityList}>
          {filteredActivity.length === 0 ? (
            <View style={styles.emptyState}>
              <Ionicons name="time-outline" size={32} color="#9CA3AF" />
              <Text style={styles.emptyStateText}>
                {searchQuery ? "No matching activity" : "No recent activity"}
              </Text>
            </View>
          ) : (
            filteredActivity.map((activity, index) => (
              <View key={index} style={styles.activityItem}>
                <View style={[styles.activityIcon, {
                  backgroundColor: activity.type === 'issue' ? '#FFFBEB' : 
                                   activity.type === 'suggestion' ? '#F0FDF4' : '#EEF2FF'
                }]}>
                  <Ionicons 
                    name={activity.type === 'issue' ? 'warning-outline' : 
                          activity.type === 'suggestion' ? 'bulb-outline' : 'document-outline'} 
                    size={16} 
                    color={activity.type === 'issue' ? '#D97706' : 
                           activity.type === 'suggestion' ? '#16A34A' : '#2563EB'} 
                  />
                </View>
                <View style={styles.activityContent}>
                  <Text style={styles.activityTitle}>{activity.title}</Text>
                  <Text style={styles.activityTime}>{activity.time}</Text>
                </View>
                {activity.status && (
                  <View style={[styles.statusBadge, {
                    backgroundColor: activity.status === 'Pending' ? '#FFFBEB' : 
                                     activity.status === 'Open' ? '#F0FDF4' : '#F1F5F9'
                  }]}>
                    <Text style={[styles.statusBadgeText, {
                      color: activity.status === 'Pending' ? '#D97706' : 
                             activity.status === 'Open' ? '#16A34A' : '#64748B'
                    }]}>{activity.status}</Text>
                  </View>
                )}
              </View>
            ))
          )}
        </View>
      </View>
    </ScrollView>
  );

  // Render tab content based on active tab
  const renderTabContent = () => {
    switch (activeTab) {
      case "dashboard":
        return renderDashboard();

      case "opportunities":
        return (
          <OpportunityListScreen
            onEditOpportunity={handleOpportunityEdit}
            onCreateOpportunity={() => handleOpportunityEdit(null)}
          />
        );

      case "issues":
        return (
          <IssueReportsScreen
            onReloadIssueCount={loadDashboardData}
          />
        );

      case "suggestions":
        return (
          <OpportunitySuggestionsScreen
            onReloadSuggestionCount={loadDashboardData}
          />
        );

      case "crawled":
        return (
          <CrawledOpportunitiesScreen
            onReloadCrawledCount={loadDashboardData}
          />
        );

      case "analytics":
        return <AnalyticsScreen />;
      case "super":
        return <SuperAdminScreen />;

      default:
        return null;
    }
  };

  if (!isAdmin) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.header}>
          <View style={styles.headerLeft}>
            <Ionicons name="shield-checkmark-outline" size={24} color="#2563EB" />
            <Text style={styles.headerTitle}>Admin Panel</Text>
          </View>
          <TouchableOpacity style={styles.closeButton} onPress={onClose}>
            <Ionicons name="close" size={24} color="#6B7280" />
          </TouchableOpacity>
        </View>

        <View style={styles.unauthorizedContainer}>
          <Ionicons name="lock-closed-outline" size={48} color="#9CA3AF" />
          <Text style={styles.unauthorizedTitle}>Access Restricted</Text>
          <Text style={styles.unauthorizedText}>
            You need administrative privileges to access this panel.
          </Text>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      {/* Clean Header */}
      <View style={styles.header}>
        <View style={styles.headerLeft}>
          <Ionicons name="shield-checkmark-outline" size={24} color="#2563EB" />
          <Text style={styles.headerTitle}>Admin Panel</Text>
        </View>
        <TouchableOpacity style={styles.closeButton} onPress={onClose}>
          <Ionicons name="close" size={24} color="#6B7280" />
        </TouchableOpacity>
      </View>

      {/* Clean Tab Bar */}
      <View style={styles.tabBar}>
        <ScrollView 
          horizontal 
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={styles.tabScrollContent}
        >
          {[
            { key: 'dashboard', label: 'Dashboard', icon: 'grid-outline' },
            { key: 'opportunities', label: 'Opportunities', icon: 'list-outline' },
            { key: 'issues', label: 'Issues', icon: 'warning-outline', badge: pendingIssuesCount },
            { key: 'suggestions', label: 'Suggestions', icon: 'bulb-outline', badge: pendingSuggestionsCount },
            { key: 'crawled', label: 'Crawled', icon: 'globe-outline', badge: pendingCrawledCount },
            { key: 'analytics', label: 'Analytics', icon: 'analytics-outline' },
            ...(isSuperAdmin ? [{ key: 'super', label: 'Super Admin', icon: 'shield-checkmark-outline' }] : []),
          ].map((tab) => (
            <TouchableOpacity
              key={tab.key}
              style={[
                styles.tabButton,
                activeTab === tab.key && styles.activeTabButton,
              ]}
              onPress={() => switchTab(tab.key as AdminTab)}
              activeOpacity={0.7}
            >
              <View style={styles.tabContent}>
                <Ionicons
                  name={tab.icon as any}
                  size={18}
                  color={activeTab === tab.key ? "#2563EB" : "#6B7280"}
                />
                <Text
                  style={[
                    styles.tabButtonText,
                    activeTab === tab.key && styles.activeTabText,
                  ]}
                >
                  {tab.label}
                </Text>
                {tab.badge && tab.badge > 0 && (
                  <View style={styles.badgeContainer}>
                    <Text style={styles.badgeText}>{tab.badge}</Text>
                  </View>
                )}
              </View>
            </TouchableOpacity>
          ))}
        </ScrollView>
      </View>

      {/* Content Area */}
      <View style={styles.contentArea}>
        {isLoading && activeTab === "dashboard" ? (
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="large" color="#2563EB" />
            <Text style={styles.loadingText}>Loading...</Text>
          </View>
        ) : (
          renderTabContent()
        )}
      </View>

      {/* Editor Modal */}
      {isEditorOpen && (
        <OpportunityEditorScreen
          opportunityId={selectedOpportunityId}
          onClose={handleCloseEditor}
        />
      )}
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#FFFFFF",
  },
  
  // Header
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingHorizontal: 20,
    paddingVertical: 16,
    backgroundColor: "#FFFFFF",
    borderBottomWidth: 1,
    borderBottomColor: "#E5E7EB",
  },
  headerLeft: {
    flexDirection: "row",
    alignItems: "center",
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: "600",
    color: "#111827",
    marginLeft: 12,
    fontFamily: "Inter-SemiBold",
  },
  closeButton: {
    width: 40,
    height: 40,
    borderRadius: 8,
    backgroundColor: "#F9FAFB",
    alignItems: "center",
    justifyContent: "center",
    borderWidth: 1,
    borderColor: "#E5E7EB",
  },

  // Tab Bar
  tabBar: {
    backgroundColor: "#FFFFFF",
    borderBottomWidth: 1,
    borderBottomColor: "#E5E7EB",
    paddingVertical: 8,
  },
  tabScrollContent: {
    paddingHorizontal: 16,
  },
  tabButton: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    marginRight: 8,
    borderRadius: 8,
    backgroundColor: "transparent",
  },
  activeTabButton: {
    backgroundColor: "#EEF2FF",
  },
  tabContent: {
    flexDirection: "row",
    alignItems: "center",
    position: "relative",
  },
  tabButtonText: {
    fontSize: 14,
    fontWeight: "500",
    color: "#6B7280",
    marginLeft: 8,
    fontFamily: "Inter-Medium",
  },
  activeTabText: {
    color: "#2563EB",
    fontWeight: "600",
    fontFamily: "Inter-SemiBold",
  },
  badgeContainer: {
    backgroundColor: "#DC2626",
    borderRadius: 10,
    minWidth: 18,
    height: 18,
    justifyContent: "center",
    alignItems: "center",
    marginLeft: 6,
    paddingHorizontal: 4,
  },
  badgeText: {
    color: "#FFFFFF",
    fontSize: 10,
    fontWeight: "bold",
    fontFamily: "Inter-Bold",
  },

  // Content Area
  contentArea: {
    flex: 1,
    backgroundColor: "#F9FAFB",
  },

  // Dashboard
  dashboardContainer: {
    flex: 1,
  },
  dashboardHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingHorizontal: 20,
    paddingVertical: 20,
    backgroundColor: "#FFFFFF",
    borderBottomWidth: 1,
    borderBottomColor: "#E5E7EB",
  },
  dashboardTitle: {
    fontSize: 24,
    fontWeight: "700",
    color: "#111827",
    fontFamily: "Inter-Bold",
  },
  lastUpdated: {
    fontSize: 14,
    color: "#6B7280",
    marginTop: 4,
    fontFamily: "Inter-Regular",
  },
  refreshButton: {
    width: 40,
    height: 40,
    borderRadius: 8,
    backgroundColor: "#F9FAFB",
    alignItems: "center",
    justifyContent: "center",
    borderWidth: 1,
    borderColor: "#E5E7EB",
  },

  // Stats Section
  statsSection: {
    padding: 20,
    backgroundColor: "#FFFFFF",
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: "600",
    color: "#111827",
    marginBottom: 16,
    fontFamily: "Inter-SemiBold",
  },
  statsGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    marginHorizontal: -8,
  },
  statCard: {
    width: "50%",
    paddingHorizontal: 8,
    marginBottom: 16,
  },
  statContent: {
    backgroundColor: "#FFFFFF",
    borderRadius: 12,
    padding: 16,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    minHeight: 80,
  },
  statInfo: {
    flex: 1,
  },
  statNumber: {
    fontSize: 28,
    fontWeight: "700",
    color: "#111827",
    fontFamily: "Inter-Bold",
  },
  statLabel: {
    fontSize: 14,
    color: "#6B7280",
    marginTop: 4,
    fontFamily: "Inter-Medium",
  },
  statIcon: {
    width: 48,
    height: 48,
    borderRadius: 8,
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },

  // Featured Opportunity Section
  featuredSection: {
    padding: 20,
    backgroundColor: "#FFFFFF",
    marginTop: 8,
  },
  featuredCard: {
    backgroundColor: "#FFFFFF",
    borderRadius: 12,
    padding: 16,
    borderWidth: 1,
    borderColor: "#E5E7EB",
  },
  featuredContent: {
    flexDirection: "row",
    alignItems: "center",
  },
  featuredIcon: {
    width: 48,
    height: 48,
    borderRadius: 8,
    alignItems: "center",
    justifyContent: "center",
    marginRight: 12,
  },
  featuredInfo: {
    flex: 1,
  },
  featuredTitle: {
    fontSize: 14,
    fontWeight: "500",
    color: "#111827",
    fontFamily: "Inter-Medium",
  },
  featuredId: {
    fontSize: 12,
    color: "#6B7280",
    marginTop: 2,
    fontFamily: "Inter-Regular",
  },
  featuredActions: {
    flexDirection: "row",
    alignItems: "center",
    marginTop: 8,
  },
  featuredActionButton: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#F9FAFB",
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 12,
    marginRight: 8,
    borderWidth: 1,
    borderColor: "#E5E7EB",
  },
  featuredActionText: {
    fontSize: 14,
    fontWeight: "500",
    color: "#2563EB",
    marginLeft: 8,
    fontFamily: "Inter-Medium",
  },
  featuredRemoveButton: {
    backgroundColor: "#FEF2F2",
    borderColor: "#FECACA",
  },
  noFeaturedContainer: {
    alignItems: "center",
    paddingVertical: 40,
  },
  noFeaturedText: {
    fontSize: 16,
    color: "#6B7280",
    marginTop: 12,
    fontFamily: "Inter-Medium",
  },
  setFeaturedButton: {
    marginTop: 16,
    paddingHorizontal: 20,
    paddingVertical: 10,
    borderRadius: 8,
    backgroundColor: "#2563EB",
  },
  setFeaturedButtonText: {
    fontSize: 14,
    fontWeight: "500",
    color: "#FFFFFF",
    fontFamily: "Inter-Medium",
  },

  // Quick Actions
  quickActionsSection: {
    padding: 20,
    backgroundColor: "#FFFFFF",
    marginTop: 8,
  },
  quickActionsGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    marginHorizontal: -6,
  },
  actionButton: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#F9FAFB",
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 12,
    marginHorizontal: 6,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: "#E5E7EB",
  },
  actionButtonUrgent: {
    backgroundColor: "#FEF2F2",
    borderColor: "#FECACA",
  },
  actionButtonText: {
    fontSize: 14,
    fontWeight: "500",
    color: "#2563EB",
    marginLeft: 8,
    fontFamily: "Inter-Medium",
  },

  // Activity Section
  activitySection: {
    padding: 20,
    backgroundColor: "#FFFFFF",
    marginTop: 8,
  },
  activityHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 16,
  },
  searchButton: {
    width: 32,
    height: 32,
    borderRadius: 6,
    backgroundColor: "#F9FAFB",
    alignItems: "center",
    justifyContent: "center",
    borderWidth: 1,
    borderColor: "#E5E7EB",
  },
  activityList: {
    backgroundColor: "#FFFFFF",
    borderRadius: 12,
    borderWidth: 1,
    borderColor: "#E5E7EB",
  },
  emptyState: {
    alignItems: "center",
    paddingVertical: 40,
  },
  emptyStateText: {
    fontSize: 16,
    color: "#6B7280",
    marginTop: 12,
    fontFamily: "Inter-Medium",
  },
  activityItem: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: "#F3F4F6",
  },
  activityIcon: {
    width: 32,
    height: 32,
    borderRadius: 6,
    alignItems: "center",
    justifyContent: "center",
    marginRight: 12,
  },
  activityContent: {
    flex: 1,
  },
  activityTitle: {
    fontSize: 14,
    fontWeight: "500",
    color: "#111827",
    fontFamily: "Inter-Medium",
  },
  activityTime: {
    fontSize: 12,
    color: "#6B7280",
    marginTop: 2,
    fontFamily: "Inter-Regular",
  },
  statusBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 6,
  },
  statusBadgeText: {
    fontSize: 11,
    fontWeight: "600",
    fontFamily: "Inter-SemiBold",
  },

  // Loading States
  loadingContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "#F9FAFB",
  },
  loadingText: {
    marginTop: 12,
    color: "#6B7280",
    fontSize: 16,
    fontFamily: "Inter-Medium",
  },

  // Unauthorized Access
  unauthorizedContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    paddingHorizontal: 24,
    backgroundColor: "#F9FAFB",
  },
  unauthorizedTitle: {
    fontSize: 20,
    fontWeight: "600",
    color: "#111827",
    marginTop: 16,
    marginBottom: 8,
    fontFamily: "Inter-SemiBold",
  },
  unauthorizedText: {
    fontSize: 16,
    color: "#6B7280",
    textAlign: "center",
    lineHeight: 24,
    fontFamily: "Inter-Regular",
  },

  // Header Actions
  headerActions: {
    flexDirection: "row",
    alignItems: "center",
  },
  exportButton: {
    width: 40,
    height: 40,
    borderRadius: 8,
    backgroundColor: "#F9FAFB",
    alignItems: "center",
    justifyContent: "center",
    borderWidth: 1,
    borderColor: "#E5E7EB",
    marginRight: 8,
  },

  // Error Banner
  errorBanner: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 20,
    paddingVertical: 12,
    backgroundColor: "#FEF2F2",
    borderWidth: 1,
    borderColor: "#FECACA",
    marginHorizontal: 20,
    marginVertical: 8,
    borderRadius: 8,
  },
  errorText: {
    fontSize: 14,
    color: "#DC2626",
    flex: 1,
    marginHorizontal: 8,
    fontFamily: "Inter-Medium",
  },

  // AI Embeddings Section
  aiEmbeddingsSection: {
    padding: 20,
    backgroundColor: "#FFFFFF",
    marginTop: 8,
  },
  aiEmbeddingsCard: {
    backgroundColor: "#FFFFFF",
    borderRadius: 12,
    padding: 16,
    borderWidth: 1,
    borderColor: "#E5E7EB",
  },
  aiEmbeddingsHeader: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 16,
  },
  aiEmbeddingsIcon: {
    width: 48,
    height: 48,
    borderRadius: 8,
    backgroundColor: "#F3E8FF",
    alignItems: "center",
    justifyContent: "center",
    marginRight: 12,
  },
  aiEmbeddingsInfo: {
    flex: 1,
  },
  aiEmbeddingsTitle: {
    fontSize: 16,
    fontWeight: "600",
    color: "#111827",
    fontFamily: "Inter-SemiBold",
  },
  aiEmbeddingsSubtitle: {
    fontSize: 14,
    color: "#6B7280",
    marginTop: 2,
    fontFamily: "Inter-Regular",
  },
  warningBox: {
    flexDirection: "row",
    alignItems: "flex-start",
    backgroundColor: "#FFFBEB",
    borderWidth: 1,
    borderColor: "#FDE68A",
    borderRadius: 8,
    padding: 12,
    marginBottom: 16,
  },
  warningText: {
    fontSize: 13,
    color: "#92400E",
    marginLeft: 8,
    flex: 1,
    lineHeight: 18,
    fontFamily: "Inter-Regular",
  },
  progressContainer: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#F8FAFC",
    borderRadius: 8,
    padding: 12,
    marginBottom: 16,
  },
  progressText: {
    fontSize: 14,
    color: "#64748B",
    marginLeft: 8,
    fontFamily: "Inter-Medium",
  },
  resultContainer: {
    flexDirection: "row",
    alignItems: "center",
    borderRadius: 8,
    padding: 12,
    marginBottom: 16,
  },
  resultSuccess: {
    backgroundColor: "#F0FDF4",
    borderWidth: 1,
    borderColor: "#BBF7D0",
  },
  resultError: {
    backgroundColor: "#FEF2F2",
    borderWidth: 1,
    borderColor: "#FECACA",
  },
  resultText: {
    fontSize: 14,
    marginLeft: 8,
    flex: 1,
    fontFamily: "Inter-Medium",
  },
  aiEmbeddingsActions: {
    flexDirection: "row",
    justifyContent: "flex-end",
  },
  embeddingButton: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 8,
    borderWidth: 1,
  },
  embeddingButtonDanger: {
    backgroundColor: "#FEF2F2",
    borderColor: "#FECACA",
  },
  embeddingButtonText: {
    fontSize: 14,
    fontWeight: "500",
    marginLeft: 8,
    fontFamily: "Inter-Medium",
  },

  // Confirmation Modal
  modalOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 1000,
  },
  confirmationModal: {
    backgroundColor: "#FFFFFF",
    borderRadius: 16,
    margin: 20,
    maxWidth: 400,
    width: "100%",
    maxHeight: "80%",
  },
  modalHeader: {
    alignItems: "center",
    paddingTop: 24,
    paddingHorizontal: 24,
    paddingBottom: 16,
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: "600",
    color: "#111827",
    marginTop: 12,
    textAlign: "center",
    fontFamily: "Inter-SemiBold",
  },
  modalContent: {
    paddingHorizontal: 24,
  },
  modalText: {
    fontSize: 16,
    color: "#374151",
    lineHeight: 24,
    textAlign: "center",
    marginBottom: 20,
    fontFamily: "Inter-Regular",
  },
  costBreakdown: {
    backgroundColor: "#F9FAFB",
    borderRadius: 8,
    padding: 16,
    marginBottom: 16,
  },
  costTitle: {
    fontSize: 14,
    fontWeight: "600",
    color: "#111827",
    marginBottom: 8,
    fontFamily: "Inter-SemiBold",
  },
  costItem: {
    fontSize: 13,
    color: "#6B7280",
    marginBottom: 4,
    fontFamily: "Inter-Regular",
  },
  costTotal: {
    fontSize: 14,
    fontWeight: "600",
    color: "#DC2626",
    marginTop: 8,
    fontFamily: "Inter-SemiBold",
  },
  warningPoints: {
    marginBottom: 16,
  },
  warningPoint: {
    fontSize: 13,
    color: "#DC2626",
    marginBottom: 6,
    fontFamily: "Inter-Regular",
  },
  modalActions: {
    flexDirection: "row",
    paddingHorizontal: 24,
    paddingBottom: 24,
    paddingTop: 8,
    justifyContent: "space-between",
  },
  modalButtonCancel: {
    flex: 1,
    paddingVertical: 12,
    borderRadius: 8,
    backgroundColor: "#F9FAFB",
    borderWidth: 1,
    borderColor: "#E5E7EB",
    marginRight: 8,
  },
  modalButtonCancelText: {
    fontSize: 16,
    fontWeight: "500",
    color: "#6B7280",
    textAlign: "center",
    fontFamily: "Inter-Medium",
  },
  modalButtonConfirm: {
    flex: 1,
    paddingVertical: 12,
    borderRadius: 8,
    backgroundColor: "#DC2626",
    marginLeft: 8,
  },
  modalButtonConfirmText: {
    fontSize: 16,
    fontWeight: "500",
    color: "#FFFFFF",
    textAlign: "center",
    fontFamily: "Inter-Medium",
  },
});

export default AdminPanel;
